import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { normalizeRows, parseCsv, serializeCsv } from "../csv/csvModel.js";
import type { EolStyle } from "../doc/eol.js";

export const NPP_EXCEL_FORMAT = "npp-excel-v1" as const;

export type NppExcelSheet = { name: string; rows: string[][] };
export type NppExcelDoc = {
  format: typeof NPP_EXCEL_FORMAT;
  activeSheetIndex: number;
  sheets: NppExcelSheet[];
};

/**
 * Coerce a formula/shared-formula result (which may itself be a Date or error object)
 * to a plain string. Extracted so both formula and shared-formula code paths use it.
 */
function cellResultToString(result: unknown): string {
  if (result instanceof Date) {
    return isNaN(result.getTime()) ? "" : result.toLocaleDateString(undefined, { dateStyle: "short" });
  }
  if (typeof result === "object" && result !== null && "error" in result) {
    return `#${(result as { error: string }).error}`;
  }
  return String(result);
}

function cellToPlainString(cell: ExcelJS.Cell): string {
  const v = cell.value;
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  // Date cells: ExcelJS returns raw Date objects; handle before the generic object branch.
  if (v instanceof Date) {
    return isNaN(v.getTime()) ? "" : v.toLocaleDateString(undefined, { dateStyle: "short" });
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if (Array.isArray((o as ExcelJS.CellRichTextValue).richText)) {
      return (o as ExcelJS.CellRichTextValue).richText.map((r) => r.text).join("");
    }
    if (typeof o.text === "string") return o.text;
    if ("formula" in o) {
      const f = o as ExcelJS.CellFormulaValue;
      if (f.result !== undefined && f.result !== null) return cellResultToString(f.result);
      return typeof f.formula === "string" ? f.formula : "";
    }
    if ("error" in o) return `#${(o as ExcelJS.CellErrorValue).error}`;
    if ("sharedFormula" in o) {
      const sf = o as ExcelJS.CellSharedFormulaValue;
      if (sf.result !== undefined && sf.result !== null) return cellResultToString(sf.result);
    }
  }
  return "";
}

function worksheetToRows(ws: ExcelJS.Worksheet): string[][] {
  const dims = ws.dimensions;
  const maxR = Math.max(1, dims?.bottom ?? ws.actualRowCount ?? ws.rowCount ?? 1);
  const maxC = Math.max(1, dims?.right ?? ws.actualColumnCount ?? ws.columnCount ?? 1);
  const rows: string[][] = [];
  for (let r = 1; r <= maxR; r++) {
    const row = ws.getRow(r);
    const line: string[] = [];
    for (let c = 1; c <= maxC; c++) {
      line.push(cellToPlainString(row.getCell(c)));
    }
    rows.push(line);
  }
  return normalizeRows(rows);
}

function sanitizeWorksheetName(name: string, index: number): string {
  const base = name.replace(/[:\\/?*[\]]/g, "_").slice(0, 31) || `Sheet${index + 1}`;
  return base;
}

export function emptyWorkbookJson(): string {
  const doc: NppExcelDoc = {
    format: NPP_EXCEL_FORMAT,
    activeSheetIndex: 0,
    sheets: [{ name: "Sheet1", rows: [[""]] }],
  };
  return JSON.stringify(doc, null, 2);
}

export function parseNppExcelDocOrNull(text: string): NppExcelDoc | null {
  try {
    const o = JSON.parse(text) as unknown;
    if (!o || typeof o !== "object") return null;
    const rec = o as Record<string, unknown>;
    if (rec.format !== NPP_EXCEL_FORMAT) return null;
    if (!Array.isArray(rec.sheets) || rec.sheets.length === 0) return null;
    const sheets: NppExcelSheet[] = [];
    for (const s of rec.sheets) {
      if (!s || typeof s !== "object") return null;
      const sh = s as Record<string, unknown>;
      if (typeof sh.name !== "string" || !Array.isArray(sh.rows)) return null;
      const rows = sh.rows.map((row) => {
        if (!Array.isArray(row)) return null;
        return row.map((cell) => (cell == null ? "" : String(cell)));
      });
      if (rows.some((x) => x === null)) return null;
      sheets.push({ name: sh.name, rows: normalizeRows(rows as string[][]) });
    }
    let activeSheetIndex = 0;
    if (typeof rec.activeSheetIndex === "number" && Number.isFinite(rec.activeSheetIndex)) {
      activeSheetIndex = Math.max(0, Math.min(sheets.length - 1, Math.floor(rec.activeSheetIndex)));
    }
    return { format: NPP_EXCEL_FORMAT, activeSheetIndex, sheets };
  } catch {
    return null;
  }
}

/** Parse an `.xlsx` / `.xlsm` file into canonical NullaPad Excel JSON (UTF-8 text buffer). */
export async function xlsxFileToNppJsonString(file: File): Promise<string> {
  const wb = new ExcelJS.Workbook();
  const buf = await file.arrayBuffer();
  await wb.xlsx.load(buf);
  const sheets: NppExcelSheet[] = [];
  wb.eachSheet((ws, order) => {
    void order;
    sheets.push({ name: ws.name || `Sheet${sheets.length + 1}`, rows: worksheetToRows(ws) });
  });
  if (sheets.length === 0) {
    sheets.push({ name: "Sheet1", rows: [[""]] });
  }
  const doc: NppExcelDoc = { format: NPP_EXCEL_FORMAT, activeSheetIndex: 0, sheets };
  return JSON.stringify(doc, null, 2);
}

/** Parse legacy `.xls` file into canonical NullaPad Excel JSON (UTF-8 text buffer). */
export async function xlsFileToNppJsonString(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(new Uint8Array(buf), { type: "array", raw: false });
  const sheets: NppExcelSheet[] = [];
  wb.SheetNames.forEach((name) => {
    const ws = wb.Sheets[name];
    if (!ws) return;
    const rows = XLSX.utils.sheet_to_json<string[]>(ws, {
      header: 1,
      blankrows: true,
      defval: "",
      raw: false,
    }).map((row) => row.map((cell) => String(cell ?? "")));
    sheets.push({ name: name || `Sheet${sheets.length + 1}`, rows: normalizeRows(rows) });
  });
  if (sheets.length === 0) {
    sheets.push({ name: "Sheet1", rows: [[""]] });
  }
  const doc: NppExcelDoc = { format: NPP_EXCEL_FORMAT, activeSheetIndex: 0, sheets };
  return JSON.stringify(doc, null, 2);
}

/** Serialize workbook JSON to an `.xlsx` `ArrayBuffer`. */
export async function nppJsonStringToXlsxBuffer(json: string): Promise<ArrayBuffer> {
  const doc = parseNppExcelDocOrNull(json);
  if (!doc) throw new Error("Invalid NullaPad Excel workbook JSON");
  const wb = new ExcelJS.Workbook();
  wb.creator = "NullaPad";
  const usedNames = new Set<string>();
  doc.sheets.forEach((s, i) => {
    let name = sanitizeWorksheetName(s.name, i);
    let n = 1;
    while (usedNames.has(name.toLowerCase())) {
      name = sanitizeWorksheetName(`${s.name}_${n++}`, i);
    }
    usedNames.add(name.toLowerCase());
    const ws = wb.addWorksheet(name);
    const rows = normalizeRows(s.rows);
    rows.forEach((row, ri) => {
      row.forEach((val, ci) => {
        ws.getCell(ri + 1, ci + 1).value = val;
      });
    });
  });
  const out = await wb.xlsx.writeBuffer();
  if (out instanceof ArrayBuffer) return out;
  const u8 = new Uint8Array(out as ArrayBufferView);
  return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength);
}

export function activeSheetCsvFromWorkbookJson(json: string, eol: EolStyle): string {
  const doc = parseNppExcelDocOrNull(json);
  if (!doc || !doc.sheets.length) return serializeCsv([[""]], eol);
  const sh = doc.sheets[doc.activeSheetIndex] ?? doc.sheets[0]!;
  return serializeCsv(normalizeRows(sh.rows), eol);
}

export function mergeActiveSheetCsvIntoWorkbookJson(json: string, csvText: string): string {
  let doc = parseNppExcelDocOrNull(json);
  if (!doc) doc = JSON.parse(emptyWorkbookJson()) as NppExcelDoc;
  const rows = normalizeRows(parseCsv(csvText));
  const idx = Math.max(0, Math.min(doc.sheets.length - 1, doc.activeSheetIndex));
  const prev = doc.sheets[idx]!;
  doc.sheets[idx] = { name: prev?.name ?? `Sheet${idx + 1}`, rows };
  return JSON.stringify(doc, null, 2);
}

export function setActiveSheetIndexInJson(json: string, index: number): string {
  const doc = parseNppExcelDocOrNull(json);
  if (!doc) return json;
  doc.activeSheetIndex = Math.max(0, Math.min(doc.sheets.length - 1, index));
  return JSON.stringify(doc, null, 2);
}
